import cookie from 'cookie'
import Cookies from 'js-cookie'
import { NextPage, NextPageContext } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import SignUp from 'modules/Home/SignUp'
import PricingPackages from 'modules/Pricing/V2'
import Testimonials from 'modules/Pricing/V2/Testimonials'
import Services from 'modules/Services/V2'

import LinkText from 'design-system/Links/LinkText'
import Body1 from 'design-system/Typography/Body/Body1'
import Caption from 'design-system/Typography/Caption/Caption'
import Heading4 from 'design-system/Typography/Heading/Heading4'

import ErrorBox from 'components/ErrorBox'
import SelectInput from 'components/GlobalStyles/Inputs/SelectInput'
import { getFormBuilderSelectInputStyles } from 'components/GlobalStyles/Inputs/utils'
import RoundedButton from 'components/RoundedButton'
import Section from 'components/Section'
import Spacer from 'components/Spacer'

import { trackGeolocatedStateForSignup, trackSelectedStateForSignup } from 'services/analytics'
import ApiCreator from 'services/api'
import { QueryParamsContext } from 'services/contexts'
import { useCloseHeaderModal, useWindowSize } from 'services/hooks'
import { usePricingPackagesForState } from 'services/swr/pricingPackages/usePricingPackagesForState'

import { sortByDateModified } from 'utils/listingHelpers'
import {
  getLocationDataFromRequest,
  getStateFromStateCode,
  setLocationCookiesServerSide,
} from 'utils/locationHelpers'
import { getPackagesForState, getWarningMessageDataForState } from 'utils/pricingPackages'

import * as T from 'types'

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
`

const HeaderTitle = styled.h1`
  font-weight: 800;
  font-size: 36px;
  line-height: 49px;
  text-align: center;
  margin: 0;
  color: ${props => props.theme.colors.neutral800};
`

interface IHighlightSubtitle {
  highlight?: boolean
}
const HeaderSubtitle = styled.span<IHighlightSubtitle>`
  font-weight: ${props => (props.highlight ? 800 : 500)};
  line-height: 33px;
  font-size: 24px;
  color: ${props => (props.highlight ? props.theme.colors.gold600 : props.theme.colors.neutral400)};
`

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  font-weight: 900;
  font-size: 12px;
  line-height: 16px;
`

interface ITextColor {
  textColor?: string
}
const Title = styled.h2<ITextColor>`
  font-weight: 800;
  font-size: 32px;
  line-height: 45px;
  margin: 0;
  text-align: center;
  color: ${props => props.textColor ?? props.theme.colors.neutral900};
`

interface ITextAlign {
  textAlign?: string
}
interface IFontSize {
  fontSize?: number
}
interface ILineHeight {
  lineHeight?: number
}
const Subtitle = styled.h3<ITextAlign & ITextColor & IFontSize & ILineHeight>`
  font-weight: 500;
  font-size: ${props => (props.fontSize ? props.fontSize : 18)}px;
  line-height: ${props => (props.lineHeight ? props.lineHeight : 25)}px;
  margin: 0;
  text-align: ${props => props.textAlign ?? 'center'};
  color: ${props => props.textColor ?? props.theme.colors.neutral400};
`

interface INoStateSelected {
  noStateSelection: boolean
}
const StateSelectContainer = styled.div<INoStateSelected>`
  display: flex;
  flex-direction: column;
  align-items: space-between;
  width: 100%;
  display: flex;
  border-radius: 16px;
  background-color: ${props =>
    props.theme.colors[props.noStateSelection ? 'transparentYellow4Alpha10' : 'white']};
  ${props => props.noStateSelection && `border: 1px solid ${props.theme.colors.gold600};`}
`

const StateSpecificWarningBanner = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  padding: 8px;
  border-radius: 0 0 16px 16px;
  background-color: ${props => props.theme.colors.gold100};
  text-align: center;
`

const StateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;
`

const InputRowContainer = styled.div`
  display: flex;
  align-items: center;
`

const SelectInputContainer = styled.div`
  width: 300px;
`

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const renderStateSpecificWarningBanner = (state: T.IState | null, formattedDate: string) => {
  if (!state || !formattedDate) return null

  return (
    <StateSpecificWarningBanner>
      <Caption>{`Our prices will change in ${state.name} on ${formattedDate}.`}</Caption>
      <Spacer size={12} />
      <Caption>
        {`Complete your listing agreement by ${formattedDate} to take advantage of our current pricing.`}
      </Caption>
    </StateSpecificWarningBanner>
  )
}

interface IProps {
  states?: T.IState[]
  defaultState: T.IState | null
  defaultStatePackages: T.IPricingPackage[]
  pricingPackageItems?: T.IPricingPackageItem[]
  serverFetchError?: string
}

const PricingPage: NextPage<IProps> = ({
  states,
  defaultState,
  defaultStatePackages,
  pricingPackageItems,
  serverFetchError,
}) => {
  const theme = useContext(ThemeContext)
  const { width } = useWindowSize()
  const isMobile = width && width < theme.metrics.tablet

  const router = useRouter()

  // query for appending UTM params
  const { params: query } = useContext(QueryParamsContext)

  // cleanup if user navigated from header modal
  useCloseHeaderModal()

  // error handling

  // if there was an error fetching data from the server, show the error until user makes a selection
  // in the state dropdown and fetches new datq
  const [shouldShowServerError, setShouldShowServerError] = useState(true)
  const showServerError = serverFetchError && shouldShowServerError

  const trackStateAndUpdateQuery = (state: T.IState) => {
    trackSelectedStateForSignup(state)
    router.query.state = state.abbreviation
    router.push(router)
  }

  // state selection
  const statesOptions = states?.map(state => ({ value: state, label: state.name })) ?? []
  const [selectedState, setSelectedState] = useState<T.IState | null>(defaultState)

  const selectState = (selection: { label: string; value: T.IState }) => {
    const newState = selection.value
    setSelectedState(newState)
    trackStateAndUpdateQuery(newState)
    setShouldShowServerError(true)
  }

  useEffect(() => {
    if (selectedState) Cookies.set('state', selectedState.abbreviation)
  }, [selectedState])

  useEffect(() => {
    trackGeolocatedStateForSignup(defaultState)
    if (defaultState) trackStateAndUpdateQuery(defaultState)
  }, [])

  // state-specific pricing packages
  const { statePricingPackages } = usePricingPackagesForState(selectedState?.abbreviation)

  // we want to show the server packages first, because they'll be instantly rendered
  // but if the user changes selection, we want to show the client-fetch, state selection packages
  const displayPackages = statePricingPackages || defaultStatePackages

  // select input custom styling
  const stylesConfig = getFormBuilderSelectInputStyles({
    theme,
    customSelectInputStyle: {
      control: {
        height: '44px',
        fontWeight: theme.fonts.weights.medium,
      },
    },
  })

  const { warningMessageIsActive, warningMessageEndDate } = getWarningMessageDataForState(
    selectedState?.abbreviation,
  )

  return (
    <>
      <Head>
        <title>List Your House For Free, Pay At Sale | HomeLister Pricing</title>
        <meta
          name="description"
          content="List your House for free, pay low fees only when you sell. Check out HomeLister's pricing packages."
        />
        <meta
          name="keywords"
          content="Free to List, Low Fees, Flat Fee MLS, Pricing, HomeLister"
        />
        <meta
          name="og:description"
          property="og:description"
          content="List your House for free, pay low fees only when you sell. Check out HomeLister's pricing packages."
        />
      </Head>
      <Section
        mobilePadding="0 18px"
        desktopPadding="0 112px"
        desktopLargePadding="0 168px"
        backgroundColor={theme.colors.grey31}
        column
      >
        <Spacer size={32} />
        <HeaderContainer>
          <HeaderTitle>Pricing plans for everyone</HeaderTitle>
          <Spacer size={16} />
          <div>
            <HeaderSubtitle>HomeLister Promise:</HeaderSubtitle>{' '}
            <HeaderSubtitle highlight>No hidden fees, fully transparent pricing</HeaderSubtitle>
          </div>
        </HeaderContainer>
        <Spacer
          size={36}
          desktopSize={56}
        />
        <StateSelectContainer noStateSelection={!selectedState}>
          <StateContainer>
            <Heading4>Select your state below to see pricing based on your local area.</Heading4>
            <Spacer size={16} />
            <InputRowContainer>
              {!isMobile && <Body1 type="tertiary">Prices for:</Body1>}
              {!isMobile && <Spacer size={16} />}
              <SelectInputContainer>
                <SelectInput
                  id="state-selection"
                  value={
                    selectedState ? { label: selectedState?.name, value: selectedState } : null
                  }
                  options={statesOptions}
                  placeholder="Choose your state"
                  onChange={selectState}
                  stylesConfig={stylesConfig}
                />
              </SelectInputContainer>
            </InputRowContainer>
          </StateContainer>
          {/* When we change prices, we put a warning message here, usually for about a week */}
          {warningMessageIsActive
            && renderStateSpecificWarningBanner(selectedState, warningMessageEndDate)}
        </StateSelectContainer>
        <Spacer size={56} />
        {showServerError && <ErrorBox error={serverFetchError} />}
        {!showServerError && displayPackages?.length && pricingPackageItems?.length && (
          <PricingPackages
            pricingPackages={displayPackages}
            items={pricingPackageItems}
            selectedState={selectedState}
          />
        )}
        <Spacer
          size={36}
          desktopSize={56}
        />
        <ButtonContainer data-testid="getStartedPricingPage">
          <RoundedButton
            text="Get Started"
            href={{
              pathname: '/listing/create',
              query: query as { [key: string]: string },
            }}
            background={theme.colors.blue4}
            color={theme.colors.white}
            radius={20}
            width={230}
          />
        </ButtonContainer>
        <Spacer
          size={32}
          desktopSize={72}
        />
      </Section>
      <Section
        mobilePadding="35px 32px"
        desktopPadding="72px 112px"
        desktopLargePadding="72px 168px"
        column
      >
        <div>
          <Title>Additional / A La Carte Services</Title>
          <Spacer size={16} />
          <Subtitle>Choose how much help you like.</Subtitle>
          <Spacer size={36} />
          <Services includeLoadServicesButton />
          <Spacer />
          <CenteredContainer>
            <LinkText
              href="/partner-offers"
              text="Partner Offers for Homeowners"
            />
          </CenteredContainer>
        </div>
      </Section>
      <Section
        mobilePadding="32px 32px 36px"
        desktopPadding="72px 70px 145px"
        desktopLargePadding="72px 165px"
        background={`linear-gradient(0deg, ${theme.colors.white} 312px, ${theme.colors.gold500} 0)`}
        desktopBackground={`linear-gradient(0deg, ${theme.colors.white} 320px, ${theme.colors.gold500} 0)`}
      >
        <Testimonials />
      </Section>
      <Spacer
        size={74}
        hideMobile
        hideTablet
      />
      <Section
        noPadding
        column
      >
        <SignUp />
      </Section>
    </>
  )
}

export const getServerSideProps = async (ctx: NextPageContext) => {
  try {
    const api = new ApiCreator(ctx)

    // pricing packages
    const pricingPackagesRes = await api.getPricingPackages()
    const pricingPackageItemsRes = await api.getPricingPackageItems()

    if (
      !pricingPackagesRes.ok
      || !pricingPackagesRes.data
      || !pricingPackageItemsRes.ok
      || !pricingPackageItemsRes.data
    ) {
      throw new Error('We were unable to load packages at this time.')
    }

    // state data (only the states we are actively selling in)
    const statesRes = await api.getStates()

    if (!statesRes.ok || !statesRes.data) {
      throw new Error('We were unable to load states at this time.')
    }

    const states = statesRes.data

    /**
     * Order of precedence for finding a default state:
     * 1. state value explicitly passed in query string (e.g. link from state landing page)
     * 2. logged in user's most recently updated listing's state
     * 3. state value stored in cookie, set when user selects a value in the state dropdown or
     *    visits a state-based landing page
     * 4. state value derived from IP address
     *
     * If no state can be found, user will be prompted to select a state from the dropdown
     */
    let defaultState: T.IState | null = null

    // first, check if there is a state query param
    // this must be first because we want to have a permanent link that we can share
    const queryState
      = (ctx.query?.state ? states.find(state => state.abbreviation === ctx.query.state) : null)
      ?? null
    if (queryState) defaultState = queryState

    // second, any existing listings if user is logged in
    if (!defaultState) {
      const listingsRes = await api.getMyListings()

      if (listingsRes.ok && listingsRes.data?.length) {
        const sortedListings = listingsRes.data.sort(sortByDateModified)
        const mostRecentListing = sortedListings[0]
        const listingState = states.find(state => state.abbreviation === mostRecentListing.state)
        if (listingState) defaultState = listingState
      }
    }

    // third check if there is a state cookie
    if (!defaultState) {
      const parsedCookies = cookie.parse(ctx.req?.headers.cookie || '')
      const cookieStateAbbreviation = parsedCookies.state
      const cookieState = states.find(state => state.abbreviation === cookieStateAbbreviation)
      if (cookieState) defaultState = cookieState
    }

    // finally, if there is no state cookie or query param, get the user's IP address
    if (!defaultState) {
      const {
        region_code: stateCode,
        latitude,
        longitude,
      } = await getLocationDataFromRequest(ctx.req)

      const userIpState = stateCode ? getStateFromStateCode(stateCode, states) : null
      if (userIpState) defaultState = userIpState

      // we store the lat/lng location here so that if we go to other pages requiring precise
      // location we can skip an additional call to the IpData API. Expires after 24 hours.
      setLocationCookiesServerSide(latitude, longitude, ctx.res)
    }

    const defaultStatePackages = getPackagesForState(
      pricingPackagesRes.data,
      defaultState?.abbreviation,
    )

    return {
      props: {
        states,
        defaultState,
        defaultStatePackages,
        pricingPackageItems: pricingPackageItemsRes.data,
      },
    }
  } catch (err: any) {
    const error = err as Error
    return { props: { serverFetchError: error.message } }
  }
}

export default PricingPage
